// Resolving a design token to a literal, for the APIs that can't evaluate CSS.
//
// Canvas 2D (`fillStyle`, `shadowColor`), WebGL, and anything that takes a colour
// as a plain STRING do not understand `var(--sym-…)` — they silently fall back to
// black. Read the token's computed value here instead of hardcoding the hex, so
// those call sites still track the design system (and a future dark theme).

/**
 * The literal value the browser has computed for a design token — pass either
 * `var(--sym-foo)` or the bare `--sym-foo`. Resolves against `<html>` by default,
 * which is where styles/design-tokens.css defines them; pass an element to pick
 * up a scoped override. Returns `fallback` when there is no DOM (SSR) or the
 * token is undefined.
 */
export function tokenValue(token: string, fallback = 'transparent', scope?: Element): string {
  if (typeof window === 'undefined') return fallback;
  const name = token.trim().replace(/^var\(\s*/, '').replace(/\s*\)$/, '');
  const el = scope ?? document.documentElement;
  return window.getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

/** `tokenValue` over a list, in order. */
export const tokenValues = (tokens: readonly string[], fallback?: string): string[] =>
  tokens.map((token) => tokenValue(token, fallback));
